import { useState, useMemo } from "react"
import { IAdditionalProductCancellationRequest } from "contexts/opus/context"
import moment from "moment"
import { useMutation } from "react-query"
import useOpus from "contexts/opus/hooks/use-opus"
import { IAdditionalProduct } from "models/policies/additionalProduct"
import {
  Alert,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Descriptions,
  Modal,
  Row,
  Space,
  Spin,
  notification
} from "antd"
import { DollarOutlined, SendOutlined } from "@ant-design/icons"
import styled from "styled-components"
import { MoneyDisplay } from "components/display/MoneyDisplay"
import { BrokerBackdateNote } from "containers/policies/views/PolicyDetails/v2_components/broker-backdate-note"
import { useBrokerBackdateLimit } from "containers/policies/services/hooks/use-broker-backdate-limit"
import {
  brokerBackdateLimitInfoMessage,
  brokerBackdateLimitMessage,
  brokerBackdateLimitRestrictsDates,
  brokerNoSelectableDateMessage,
  brokerHasNoSelectableCancellationDate,
  isBeforeBrokerBackdateLimit,
  isCancellationDateDisabled
} from "utils/pocketknife/cancellation-date"

const StyledRow = styled(Row)`
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const StyledCol = styled(Col)`
  & > * {
    margin-bottom: 8px;
    :last-child {
      margin-bottom: 0;
    }
  }
`

const StyledDiv = styled("div")`
  margin-bottom: 20px;
`

const StyledDatepicker = styled(DatePicker)`
  width: 100%;
`

export const AdditionalProductCancellation: React.FC<IAdditionalProductCancellationProps> =
  ({
    policyID,
    policyCoverRange,
    policyPurchaseDate,
    policyLineOfBusiness,
    refetchPolicyBundle,
    refetchTimelineEvents,
    additionalProduct,
    isBrokerProduct,
    productBrokerMaxBackdateDays
  }) => {
    const [modalVisible, setModalVisible] = useState(false)
    const brokerMaxBackdateDays = useBrokerBackdateLimit(
      productBrokerMaxBackdateDays
    )

    const minCancellationDate = useMemo(() => {
      if (policyPurchaseDate && policyCoverRange && policyCoverRange.length) {
        const purchaseDate = moment.utc(policyPurchaseDate).startOf("day")
        const coverStart = moment.utc(policyCoverRange[0]).startOf("day")

        if (policyLineOfBusiness === "motor") {
          return coverStart
        } else if (policyLineOfBusiness === "travel") {
          return purchaseDate
        }

        return moment.min([purchaseDate, coverStart])
      }
    }, [policyLineOfBusiness, policyPurchaseDate, policyCoverRange])

    const coverRange = useMemo(() => {
      if (policyCoverRange && policyCoverRange.length) {
        return [
          moment.utc(policyCoverRange[0]),
          moment.utc(policyCoverRange[1]).endOf("day")
        ]
      }
    }, [policyCoverRange])

    const defaultDate = useMemo(() => {
      const today = moment.utc().startOf("day")

      if (
        !coverRange ||
        (coverRange &&
          minCancellationDate &&
          coverRange[1].isAfter(today) &&
          (minCancellationDate.isBefore(today) ||
            minCancellationDate.isSame(today)))
      ) {
        return today
      } else if (minCancellationDate && minCancellationDate.isAfter(today)) {
        return minCancellationDate
      }

      return coverRange[1]
    }, [minCancellationDate, coverRange])

    const opusClient = useOpus()

    const [cancellationRequest, setCancellationRequest] =
      useState<IAdditionalProductCancellationRequest>({
        issueRefund: isBrokerProduct ? false : true,
        sendEmail: isBrokerProduct ? false : true,
        additionalProductReferenceID: additionalProduct.referenceID,
        effectiveCancellationDate: defaultDate.format()
      })

    const cancellationInfoMutation = useMutation(
      (request: IAdditionalProductCancellationRequest) =>
        opusClient.policyClient.getAdditionalProductCancellationInfo(
          policyID,
          request
        )
    )

    const cancellationMutation = useMutation(
      (request: IAdditionalProductCancellationRequest) =>
        opusClient.policyClient.additionalProductCancellation(policyID, request)
    )

    const onCloseModal = () => {
      setModalVisible(false)
    }

    const noSelectableDate = brokerHasNoSelectableCancellationDate(
      coverRange?.[1],
      brokerMaxBackdateDays
    )

    const handleCancellation = async () => {
      // The picker disables these dates, but the window can move past a
      // selected date if the modal is left open over midnight.
      if (
        brokerMaxBackdateDays !== undefined &&
        isBeforeBrokerBackdateLimit(
          moment.utc(cancellationRequest.effectiveCancellationDate),
          brokerMaxBackdateDays
        )
      ) {
        notification.error({
          message: brokerBackdateLimitMessage(brokerMaxBackdateDays)
        })
        return
      }

      if (policyID) {
        await cancellationMutation
          .mutateAsync(cancellationRequest)
          .then(() => {
            setModalVisible(false)
            notification.success({
              message: "Cancellation successful."
            })
            refetchPolicyBundle && refetchPolicyBundle()
            refetchTimelineEvents && refetchTimelineEvents()
          })
          .catch(() => {
            notification.error({
              message: `An error occurred when attempting to cancel ${additionalProduct?.name}. Please try again, or contact support.`
            })
          })
      }
    }

    const fetchCancellationInfo = (
      request: IAdditionalProductCancellationRequest
    ) => {
      cancellationInfoMutation.mutateAsync(request)
    }

    const onCancelClick = () => {
      setModalVisible(true)
      fetchCancellationInfo(cancellationRequest)
    }

    return (
      <>
        <Button loading={false} onClick={onCancelClick}>
          Cancel {additionalProduct?.name}
        </Button>
        <Modal
          title={`Cancel ${additionalProduct?.name}`}
          visible={modalVisible}
          onCancel={onCloseModal}
          footer={
            <Space direction="horizontal">
              <Button onClick={onCloseModal}>Close</Button>
              <Button
                type="primary"
                onClick={handleCancellation}
                loading={cancellationMutation.isLoading}
                disabled={noSelectableDate}
              >
                Cancel {additionalProduct?.name}
              </Button>
            </Space>
          }
        >
          <Spin spinning={cancellationInfoMutation.isLoading}>
            <StyledDiv>
              <h2>Policy Details</h2>
              <Descriptions
                size="small"
                column={1}
                style={{ margin: "0 0 20px 0" }}
              >
                <Descriptions.Item label="Cover period start">
                  {coverRange ? coverRange[0].format("DD/MM/YYYY") : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Cover period end">
                  {coverRange ? coverRange[1].format("DD/MM/YYYY") : "-"}
                </Descriptions.Item>
              </Descriptions>
              <h2>Cancellation Details</h2>
              <h4>Effective Date</h4>
              <StyledDatepicker
                disabledDate={(current) =>
                  isCancellationDateDisabled(current, {
                    minCancellationDate,
                    coverEndDate: coverRange?.[1],
                    brokerMaxBackdateDays
                  })
                }
                id="effectiveCancellationDate"
                format="DD/MM/YYYY"
                allowClear={false}
                value={moment(cancellationRequest.effectiveCancellationDate)}
                onChange={(date) => {
                  const request = {
                    ...cancellationRequest,
                    effectiveCancellationDate:
                      date?.startOf("day")?.format("YYYY-MM-DDTHH:mm:ss") + "Z"
                  }
                  setCancellationRequest(request)
                  fetchCancellationInfo(request)
                }}
              />
              {brokerMaxBackdateDays !== undefined &&
                (noSelectableDate ? (
                  <Alert
                    type="warning"
                    message={brokerNoSelectableDateMessage(
                      brokerMaxBackdateDays
                    )}
                    style={{ marginTop: 8 }}
                  />
                ) : (
                  brokerBackdateLimitRestrictsDates(
                    minCancellationDate,
                    brokerMaxBackdateDays
                  ) && (
                    <BrokerBackdateNote style={{ marginTop: 8 }}>
                      {brokerBackdateLimitInfoMessage(brokerMaxBackdateDays)}
                    </BrokerBackdateNote>
                  )
                ))}
              <Descriptions
                size="small"
                column={1}
                style={{ margin: "20px 0 0 0" }}
              >
                <Descriptions.Item label="Refund Due">
                  {cancellationInfoMutation?.data?.refundAmount ===
                  undefined ? (
                    "$-"
                  ) : (
                    <MoneyDisplay
                      value={cancellationInfoMutation?.data?.refundAmount}
                    />
                  )}
                </Descriptions.Item>
                {cancellationInfoMutation?.data?.availableRefund != null && (
                  <>
                    <Descriptions.Item label="Total Cleared Payments">
                      <MoneyDisplay
                        value={
                          cancellationInfoMutation.data.totalClearedPayments
                        }
                      />
                    </Descriptions.Item>
                    <Descriptions.Item label="Total Refunds Issued">
                      <MoneyDisplay
                        value={cancellationInfoMutation.data.totalRefundsIssued}
                      />
                    </Descriptions.Item>
                    <Descriptions.Item label="Available Refund">
                      <MoneyDisplay
                        value={cancellationInfoMutation.data.availableRefund}
                      />
                    </Descriptions.Item>
                  </>
                )}
              </Descriptions>
            </StyledDiv>
            {!isBrokerProduct && (
              <>
                <h2>Actions</h2>
                <StyledCol>
                  <StyledRow>
                    <h4>
                      <DollarOutlined /> Issue refund (
                      {cancellationInfoMutation?.data?.refundAmount ===
                      undefined ? (
                        "$-"
                      ) : (
                        <MoneyDisplay
                          value={cancellationInfoMutation?.data?.refundAmount}
                        />
                      )}
                      )
                    </h4>
                    <Checkbox
                      id="issueRefund"
                      onChange={(event) => {
                        setCancellationRequest((prevRequest) => ({
                          ...prevRequest,
                          issueRefund: event.target.checked
                        }))
                      }}
                      disabled={
                        cancellationInfoMutation?.data?.refundAmount?.value !=
                          null &&
                        cancellationInfoMutation?.data?.refundAmount?.value < 0
                      }
                      checked={
                        cancellationInfoMutation?.data?.refundAmount?.value !=
                          null &&
                        cancellationInfoMutation?.data?.refundAmount?.value >=
                          0 &&
                        cancellationRequest.issueRefund
                      }
                    />
                  </StyledRow>
                </StyledCol>
                <StyledCol>
                  <StyledRow>
                    <h4>
                      <SendOutlined /> Send cancellation email to the customer
                    </h4>
                    <Checkbox
                      id="sendCancellationEmail"
                      onChange={(event) => {
                        setCancellationRequest((prevRequest) => ({
                          ...prevRequest,
                          sendEmail: event.target.checked
                        }))
                      }}
                      checked={cancellationRequest.sendEmail}
                    />
                  </StyledRow>
                </StyledCol>
              </>
            )}
          </Spin>
        </Modal>
      </>
    )
  }

interface IAdditionalProductCancellationProps {
  policyID: string
  policyCoverRange?: string[]
  policyPurchaseDate?: string
  policyLineOfBusiness?: string
  refetchPolicyBundle?: VoidFunction
  refetchTimelineEvents?: VoidFunction
  additionalProduct: IAdditionalProduct
  isBrokerProduct: boolean
  // The policy's product brokerMaxCancellationBackdateDays setting
  productBrokerMaxBackdateDays?: number
}

export default AdditionalProductCancellation
